import { ArrowRight, Check, MapPin } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MultiStepForm from "@/components/MultiStepForm";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { catByKey, cityPath, cityDisplayName } from "@/i18n/slugs";
import { cityInfoNL } from "@/i18n/content-nl";

const cityInfo = {
  Bruxelles: {
    region: "Bruxelles-Capitale",
    description: "Capitale de la Belgique, Bruxelles offre un marché dynamique pour les travaux de rénovation et construction."
  },
  Namur: {
    region: "Wallonie",
    description: "Chef-lieu de la Wallonie, Namur combine patrimoine historique et modernité pour vos projets de rénovation."
  },
  Luxembourg: {
    region: "Grand-Duché",
    description: "La ville de Luxembourg, capitale du Grand-Duché, offre un marché premium pour les travaux de construction."
  },
  Liège: {
    region: "Wallonie",
    description: "Troisième ville de Belgique, Liège est un centre économique majeur avec de nombreux projets de rénovation."
  },
  Charleroi: {
    region: "Hainaut",
    description: "Ville en pleine transformation, Charleroi connaît un renouveau urbain avec de nombreux projets de rénovation et construction."
  },
  Arlon: {
    region: "Luxembourg (BE)",
    description: "Chef-lieu de la province de Luxembourg, Arlon est idéalement située à la frontière du Grand-Duché pour vos projets transfrontaliers."
  },
  "Marche-en-Famenne": {
    region: "Luxembourg (BE)",
    description: "Au cœur de la Famenne, Marche-en-Famenne est une ville dynamique entourée de nature, idéale pour les projets de construction durable."
  },
  Bastogne: {
    region: "Luxembourg (BE)",
    description: "Ville historique des Ardennes, Bastogne allie patrimoine et modernité avec de nombreux projets de rénovation résidentielle."
  },
  "Luxembourg-province": {
    region: "Luxembourg (BE)",
    description: "La province de Luxembourg, plus grande province de Belgique, offre un cadre naturel exceptionnel pour vos projets de construction."
  },
  Mons: {
    region: "Hainaut",
    description: "Capitale culturelle, Mons séduit par son patrimoine architectural et ses nombreux projets de rénovation urbaine."
  },
  Nivelles: {
    region: "Brabant wallon",
    description: "Au cœur du Brabant wallon, Nivelles bénéficie d'un marché immobilier dynamique et de nombreux projets de construction."
  },
  "Luxembourg-Ville": {
    region: "Grand-Duché",
    description: "Capitale européenne, Luxembourg-Ville offre un marché premium avec une forte demande en rénovation et construction haut de gamme."
  },
  "Esch-sur-Alzette": {
    region: "Grand-Duché",
    description: "Deuxième ville du Grand-Duché, Esch-sur-Alzette connaît un développement urbain important avec de nombreux chantiers."
  },
  Differdange: {
    region: "Grand-Duché",
    description: "Troisième ville du Luxembourg, Differdange se modernise avec des projets résidentiels et commerciaux ambitieux."
  }
};

export default function CityPage({ category, city }) {
  const { lang, T } = useLang();
  const C = T.city;
  const cat = catByKey(category);
  const categoryName = cat ? cat[lang].nav : (lang === "nl" ? "Werken" : "Travaux");
  const info = lang === "nl"
    ? (cityInfoNL[city] || cityInfoNL.Bruxelles)
    : (cityInfo[city] || cityInfo.Bruxelles);
  const displayCity = cityDisplayName(city, lang);
  const frPath = cityPath(category, city, "fr");
  const nlPath = cityPath(category, city, "nl");
  const pageUrl = `https://devismaison.be${lang === "nl" ? nlPath : frPath}`;
  const pageTitle = lang === "nl"
    ? `Offerte ${categoryName} ${displayCity} | DevisMaison.be`
    : `Devis ${categoryName} ${city} | DevisMaison.be`;
  const pageDesc = C.metaDesc(categoryName.toLowerCase(), displayCity, info.region);

  return (
    <div className="min-h-screen bg-white" data-testid={`city-page-${category}-${city.toLowerCase()}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": pageTitle,
          "description": pageDesc,
          "provider": { "@type": "LocalBusiness", "name": "DevisMaison.be", "url": "https://devismaison.be" },
          "areaServed": { "@type": "City", "name": displayCity },
          "url": pageUrl
        })}</script>
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DevisMaison.be" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
      <Hreflang frPath={frPath} nlPath={nlPath} />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 hero-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 mb-6">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-slate-700">{displayCity}, {info.region}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                {C.titlePrefix} {categoryName} {C.at}{" "}
                <span className="text-orange-500">{displayCity}</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {C.heroSub(categoryName.toLowerCase(), displayCity)}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="#lead-form">
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 rounded-full text-lg group"
                    data-testid="city-cta-button"
                  >
                    {C.cta}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{C.localArtisans}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{C.noCommit}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div
              id="lead-form"
              className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 lg:p-8 border border-slate-100"
            >
              <MultiStepForm initialCategory={category} initialCity={city} />
            </div>
          </div>
        </div>
      </section>

      {/* City Info Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-6">
                {categoryName} {C.at} {displayCity}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                {info.description}{C.networkText(categoryName.toLowerCase())}
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {C.whyTitle(displayCity)}
              </h3>
              <ul className="space-y-3 mb-8">
                {C.whyItems.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-600">
                    <Check className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right - Stats */}
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 mb-8">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
                  {C.statsTitle(displayCity)}
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-heading font-bold text-orange-500">50+</p>
                    <p className="text-sm text-slate-600 mt-1">{C.statPartners}</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-heading font-bold text-orange-500">500+</p>
                    <p className="text-sm text-slate-600 mt-1">{C.statProjects}</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-heading font-bold text-orange-500">4.8/5</p>
                    <p className="text-sm text-slate-600 mt-1">{C.statSatisfaction}</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-heading font-bold text-orange-500">48h</p>
                    <p className="text-sm text-slate-600 mt-1">{C.statDelay}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-orange-50 rounded-xl border border-orange-100">
                <h4 className="font-heading font-semibold text-slate-900 mb-2">
                  {C.zoneTitle}
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  {C.zoneText(displayCity)}
                </p>
                <a href="#lead-form" className="text-orange-500 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                  {C.zoneLink} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            {C.ctaTitle(displayCity)}
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            {C.ctaSub}
          </p>
          <a href="#lead-form">
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 rounded-full text-lg"
              data-testid="city-cta-bottom-button"
            >
              {C.ctaBtn}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
